import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router';

import ContentArea from '../../components/contentArea';
import Navigation from '../../components/navigation';
import InputField from '../../components/formItem/inputField';
import Toggle from '../../components/toggle';
import Button from '../../components/button';
import { FormLabel } from '../../components/formItem/formLabel';
import RadioBt, { RadioGroup } from '../../components/radioBt';
import { championAPI } from '../../actions/imsServer';
import Popup from '../../components/popup';
import { CHAMPIONSHIP_TYPES, TOURNAMENT_PARTICIPANT_OPTIONS } from '../../constants';
import { formatDateForMySQL } from '../../utils/base';

export const ChampionshipUserAdd = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [popup, setPopup] = useState({
        open: false,
        message: '',
        subMessage: '',
        onConfirm: null
    });
    const [meInfo, setMeInfo] = useState(null);

    useEffect(() => {
        const meInfo = JSON.parse(localStorage.getItem('meInfo'));
        setMeInfo(meInfo);
    }, []);

    // 폼 상태 관리
    const [formData, setFormData] = useState({
        name: '',
        organizationId: location.state?.organizationId || meInfo?.mainOrganizationId,
        userId: meInfo?.id,
        contentIndex: '',
        contentName: '',
        type: 0,
        state: 0,
        endDate: '',
        period: 7, // 랭킹 기본값 7일
        playerCount: 16, // 토너먼트 기본값 16강
        isPublic: true,
        password: '',
        rewardMessage: '',
    });

    // 대회 기간 옵션 (토너먼트용) - constants 공통 상수 사용, 표시용으로 t() 적용
    const tournamentPeriodOptions = TOURNAMENT_PARTICIPANT_OPTIONS.map(option => ({ value: option.value, label: t(option.label) }));

    // 대회 기간 옵션 (랭킹용)
    const rankingPeriodOptions = [{ value: 7, label: t('7_days') }, { value: 14, label: t('14_days') }, { value: 30, label: t('30_days') }, { value: 60, label: t('60_days') }];

    // 초기 endDate 설정 (랭킹일 경우)
    useEffect(() => {
        const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');
        if (formData.type === 0 && formData.period) {
            const endDate = new Date(new Date().getTime() + formData.period * 24 * 60 * 60 * 1000);
            // 해당일의 23시 59분 59초로 설정
            endDate.setHours(23, 59, 59, 999);
            setFormData(prev => ({
                ...prev,
                organizationId: prev.organizationId || location.state?.organizationId || meInfo?.mainOrganizationId,
                userId: prev.userId || meInfo?.id,
                endDate: prev.endDate || endDate
            }));
        }
    }, [location.state, meInfo?.mainOrganizationId, meInfo?.id]);

    // 종목 선택 페이지에서 돌아온 데이터 처리
    useEffect(() => {
        if (location.state?.formData) {
            setFormData(prev => ({
                ...prev,
                ...location.state.formData
            }));
        }
    }, [location.state?.formData]);

    const showPopup = (message, subMessage = '', onConfirm) => {
        setPopup({
            open: true,
            message,
            subMessage,
            onConfirm
        });
    };

    // 폼 데이터 업데이트 함수
    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const newData = {
                ...prev,
                [field]: value
            };

            if (field === 'type') {
                if (value === 0) {
                    // 랭킹: 기존 period 값 유지 (없으면 7일 기본값)
                    const periodToUse = newData.period && newData.period > 0 ? newData.period : 7;
                    newData.period = periodToUse;
                    const endDate = new Date(new Date().getTime() + periodToUse * 24 * 60 * 60 * 1000);
                    // 해당일의 23시 59분 59초로 설정
                    endDate.setHours(23, 59, 59, 999);
                    newData.endDate = endDate;
                    // playerCount는 유지 (토너먼트로 돌아올 때 사용)
                } else {
                    // 토너먼트: 기존 playerCount 값 유지 (없으면 16강 기본값)
                    const playerCountToUse = newData.playerCount && newData.playerCount > 0 ? newData.playerCount : 16;
                    newData.playerCount = playerCountToUse;
                    newData.endDate = null;
                    // period는 유지 (랭킹으로 돌아올 때 사용)
                }
            } else if (field === 'period') {
                if (formData.type === 0) {
                    const endDate = new Date(new Date().getTime() + value * 24 * 60 * 60 * 1000);
                    // 해당일의 23시 59분 59초로 설정
                    endDate.setHours(23, 59, 59, 999);
                    newData.endDate = endDate;
                    newData.playerCount = null;
                } else {
                    newData.endDate = null;
                    newData.playerCount = value;
                }
            }

            // 공개로 변경되면 비밀번호 초기화
            if (field === 'isPublic' && value === true) {
                newData.password = '';
            }

            return newData;
        });
    };

    // 취소 버튼 클릭
    const handleCancel = () => {
        navigate('/championships');
    };

    // 유효성 검사 함수
    const isValid = () => {
        // 대회이름: 필수, 24자 이내
        if (!formData.name || formData.name.trim() === '' || formData.name.length > 24) {
            return false;
        }
        // 대회종목: 필수
        if (!formData.contentIndex) {
            return false;
        }
        // 대회기간: 랭킹일 경우 period 필수, 토너먼트일 경우 playerCount 필수
        if (formData.type === 0 && !formData.period) {
            return false;
        }
        if (formData.type === 1 && !formData.playerCount) {
            return false;
        }
        // 비공개인 경우 비밀번호 필수 (4자리)
        if (!formData.isPublic && (!formData.password || formData.password.length !== 4)) {
            return false;
        }
        return true;
    };

    // 확인 버튼 클릭
    const handleConfirm = async () => {
        // 비공개인 경우 비밀번호 유효성 검사
        if (!formData.isPublic && formData.password.length !== 4) {
            alert(t('please_enter_a_4_digit_password'));
            return;
        }

        const organizationIdNumber =
            formData.organizationId == null || formData.organizationId === ''
                ? null
                : Number(formData.organizationId);

        // 제출 시 날짜: startDate=현재 시각(로컬), endDate=랭킹 시 D+period 23:59:59(로컬) → formatDateForMySQL가 로컬 YYYY-MM-DD HH:mm:ss 반환
        const submitData = {
            ...formData,
            organizationId: Number.isFinite(organizationIdNumber) ? organizationIdNumber : null,
            startDate: formData.startDate || formatDateForMySQL(new Date()),
            endDate: formData.endDate ? formatDateForMySQL(formData.endDate) : null,
            playerCount: formData.type === 0 ? null : formData.playerCount,
        };

        const response = await championAPI.registerChampion(submitData);

        if (response.result === 'created') {
            showPopup(t('championship_has_been_created'), '', () => {
                navigate('/championships');
            });
        } else {
            showPopup(t('championship_creation_failed'), '', () => {
                navigate('/championships');
            });
        }
    };

    return (
        <ContentArea sx={{
            padding: '0 10px'
        }}>
            <Navigation title={t('create_championship_room')} showLeftIcon={false} />

            {/* Form Content */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '60px',
                zIndex: 1,
                width: '100%'
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    alignItems: 'center',
                    marginBottom: '31px'
                }}>
                    {/* 대회이름 */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        width: '100%',
                        alignItems: 'flex-start',
                    }}>
                        <FormLabel text={t('championship_name')} required />
                        <InputField
                            placeholder={t('max_24_characters')}
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            error={!!formData.name && formData.name.length > 24}
                            helperText={(formData.name && formData.name.length > 24) ? t('please_enter_within_24_characters') : ' '}
                        />
                    </Box>

                    {/* 대회종목 */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        width: '100%',
                        alignItems: 'flex-start',

                    }}>
                        <FormLabel text={t('championship_event')} required />
                        <Box
                            sx={{
                                width: '100%',
                                height: '40px',
                                backgroundColor: theme.palette.figma.white[20],
                                borderRadius: '15px',
                                border: '1px solid ' + theme.palette.figma.white[30],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0 15px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: theme.palette.figma.white[20],
                                }
                            }}
                            onClick={() => navigate('/championships/content-selection', {
                                state: { formData }
                            })}
                        >
                            <Typography sx={{
                                fontSize: '14px',
                                color: formData.contentName ? theme.palette.figma.white[100] : theme.palette.figma.white[60],
                                letterSpacing: '-0.42px'
                            }}>
                                {formData.contentName || t('select')}
                            </Typography>
                            <Typography sx={{
                                fontSize: '12px',
                                color: theme.palette.figma.white[60],
                                transform: 'rotate(90deg)'
                            }}>
                                ›
                            </Typography>
                        </Box>
                    </Box>

                    {/* 대회형식 */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        width: '100%',
                        alignItems: 'flex-start'
                    }}>
                        <FormLabel text={t('championship_format')} required />
                        <Toggle
                            options={CHAMPIONSHIP_TYPES.slice().reverse().map(type => t(type.label))}
                            value={CHAMPIONSHIP_TYPES.slice().reverse().findIndex(type => type.value === formData.type)}
                            onChange={(index) => {
                                const reversedTypes = CHAMPIONSHIP_TYPES.slice().reverse();
                                const selectedType = reversedTypes[index];
                                if (selectedType) {
                                    handleInputChange('type', selectedType.value);
                                }
                            }}
                        />
                    </Box>

                    {/* 대회기간 */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        width: '100%',
                        alignItems: 'flex-start'
                    }}>
                        <FormLabel text={t('championship_period')} required />
                        <Toggle
                            variableWidth
                            options={formData.type === 0 ? rankingPeriodOptions.map(option => option.label) : tournamentPeriodOptions.map(option => option.label)}
                            value={formData.type === 0
                                ? (rankingPeriodOptions.findIndex(option => option.value === formData.period) >= 0
                                    ? rankingPeriodOptions.findIndex(option => option.value === formData.period)
                                    : 0)
                                : (tournamentPeriodOptions.findIndex(option => option.value === formData.playerCount) >= 0
                                    ? tournamentPeriodOptions.findIndex(option => option.value === formData.playerCount)
                                    : 0)
                            }
                            onChange={(index) => {
                                if (formData.type === 0) {
                                    const selectedPeriod = rankingPeriodOptions[index];
                                    if (selectedPeriod) {
                                        handleInputChange('period', selectedPeriod.value);
                                    }
                                } else {
                                    if (formData.type === 1) {
                                        const selectedPeriod = tournamentPeriodOptions[index];
                                        if (selectedPeriod) {
                                            handleInputChange('period', selectedPeriod.value);
                                        }
                                    }
                                }
                            }}
                        />
                    </Box>

                    {/* 공개여부 */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        width: '100%',
                        alignItems: 'flex-start'
                    }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%'
                        }}>
                            <FormLabel text={t('visibility')} />
                            <RadioGroup
                                value={formData.isPublic ? t('public') : t('private')}
                                onChange={(e) => {
                                    const isPublic = e.target.value === t('public');
                                    handleInputChange('isPublic', isPublic);
                                }}
                                name="publicity-group"
                            >
                                <RadioBt value={t('public')} label={t('public')} />
                                <RadioBt value={t('private')} label={t('private')} />
                            </RadioGroup>
                        </Box>

                        {/* 비공개일 때 비밀번호 입력 필드 */}
                        {!formData.isPublic && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                width: '100%',
                                paddingLeft: '20px'
                            }}>
                                <Typography sx={{
                                    fontSize: '14px',
                                    color: theme.palette.figma.white[60],
                                    letterSpacing: '-0.42px',
                                    minWidth: '60px'
                                }}>
                                    {t('password')}
                                </Typography>
                                <InputField
                                    placeholder={t('4_digits')}
                                    value={formData.password}
                                    onChange={(e) => handleInputChange('password', e.target.value)}
                                    maxLength={4}
                                    type="password"
                                    showVisibilityIcon={true}
                                    sx={{ flex: 1, height: '40px' }}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* 보상조건 */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        width: '100%',
                        alignItems: 'flex-start'
                    }}>
                        <FormLabel text={t('reward_condition')} />

                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            value={formData.rewardMessage}
                            onChange={(e) => handleInputChange('rewardMessage', e.target.value)}
                            placeholder={t('enter')}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: theme.palette.figma.white[20],
                                    borderRadius: '15px',
                                    padding: '10px 15px',
                                    '& fieldset': {
                                        border: '1px solid ' + theme.palette.figma.white[30],
                                    },
                                    '&:hover fieldset': {
                                        border: '1px solid ' + theme.palette.figma.white[40],
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: '1px solid ' + theme.palette.figma.white[50],
                                    },
                                    '& textarea': {
                                        color: theme.palette.text.main,
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        letterSpacing: '-0.42px',
                                        lineHeight: '1.5',
                                        padding: 0,
                                        '&::placeholder': {
                                            color: theme.palette.figma.white[40],
                                            opacity: 1,
                                        },
                                    },
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Bottom Buttons */}
            <Box sx={{
                display: 'flex',
                width: '100%',
                flexDirection: 'row',
                gap: '10px',
                padding: '0 0px 54px',
                flexShrink: 0,
                zIndex: 1
            }}>
                <Button
                    variant="gray"
                    onClick={handleCancel}
                    size="large"
                    sx={{ flex: 1 }}
                >
                    {t('cancel')}
                </Button>
                <Button
                    variant="normal"
                    onClick={handleConfirm}
                    size="large"
                    sx={{ flex: 1 }}
                    disabled={!isValid()}
                >
                    {t('confirm')}
                </Button>

            </Box>

            <Popup
                open={popup.open}
                message={popup.message}
                subMessage={popup.subMessage}
                onConfirm={popup.onConfirm || (() => setPopup({ ...popup, open: false }))}
            />
        </ContentArea >
    );
};

export default ChampionshipUserAdd;
